import { Avatar, Button, Pagination } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EmployeeUser } from "../../types";
import { DeleteEmployee } from "../teams/delete-employee";
import { ToggleEmployeeOnLeave } from "../teams/toggle-employee-on-leave";
import profileGenericPic from "../../assets/img/logos/profileGenericPic.jpg";
import { useEffect, useMemo, useState } from "react";
import { QrDisplayConfig } from "../../types/qr-display-config";
import { QrDisplayConfigService } from "../../services/qr-display-config-service";
import { ApplyQrDisplayConfigBar } from "../teams/apply-qr-display-config-bar";
import { useToast } from "../../context/toast-context";

interface EmployeesTabProps {
  employees: EmployeeUser[];
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onEditEmployee: (employee: EmployeeUser) => void;
  onDeleteEmployee: (employeeId: number) => void;
  onUpdateEmployees: (updatedEmployees: EmployeeUser[]) => void;
  onOnLeaveChange: (userId: number, branchId: number, onLeave: boolean) => void;
  onApplyQrConfigToAll: (qrDisplayConfigId: number) => void;
  onGoToPage: (page: number) => void;
  onGoToNextPage: () => void;
  onGoToPreviousPage: () => void;
  branchId?: number;
}

export function EmployeesTab({
  employees,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onEditEmployee,
  onDeleteEmployee,
  onUpdateEmployees,
  onOnLeaveChange,
  onApplyQrConfigToAll,
  onGoToPage,
  onGoToNextPage,
  onGoToPreviousPage,
  branchId,
}: EmployeesTabProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [qrConfigs, setQrConfigs] = useState<QrDisplayConfig[]>([]);
  const [bulkQrConfigId, setBulkQrConfigId] = useState<number | "">("");
  const qrDisplayConfigService = useMemo(() => new QrDisplayConfigService(), []);

  useEffect(() => {
    qrDisplayConfigService.getConfigs(
      (data) => {
        setQrConfigs(data);
      },
      (errorMessage) => showToast(errorMessage, "error"),
      branchId
    );
  }, [qrDisplayConfigService, showToast, branchId]);

  return (
    <div className="flex flex-col gap-4 -mt-4">
      {!isLoading && employees.length > 0 && (
        <div className="flex justify-start">
          <ApplyQrDisplayConfigBar
            configs={qrConfigs}
            selectedConfigId={bulkQrConfigId}
            onSelectedConfigIdChange={setBulkQrConfigId}
            onApplyToAll={() => {
              if (bulkQrConfigId !== "" && totalCount > 1) {
                onApplyQrConfigToAll(Number(bulkQrConfigId));
              }
            }}
            light
            employeesCount={totalCount}
          />
        </div>
      )}
      {!isLoading && employees.length > 0 && (
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="text-sm text-gray-600 font-montserrat">
            Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} de {totalCount} empleados
          </div>
          <div className="text-sm text-gray-600 font-montserrat">
            Página {currentPage + 1} de {totalPages}
          </div>
        </div>
      )}
      
      {isLoading && (
        <div className="w-full flex justify-center items-center py-8">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce"></div>
          </div>
        </div>
      )}
      {!isLoading && employees.length === 0 && (
        <div className="w-full flex justify-center items-center py-8">
          <p className="text-gray-500 font-montserrat">
            {t("branches.view.empty")}
          </p>
        </div>
      )}
      {!isLoading &&
        employees.length > 0 &&
        employees.map((employee) => {
          const emtpyGroup =
            employee.userGroups.length === 0 ||
            (employee.userGroups.length === 1 &&
              employee.userGroups[0].id === null);

          return (
            <div
              key={employee.userId}
              className="flex justify-between items-center w-full mb-4"
            >
              <div className="flex w-full items-center gap-2">
                <Avatar
                  sx={{
                    width: "2.5rem",
                    height: "2.5rem",
                  }}
                  src={
                    employee.imageUrl ??
                    profileGenericPic
                  }
                  alt="Avatar Usuario"
                />
                <div className="flex flex-col items-start justify-center">
                  <p className="font-semibold text-sm">
                    {employee.name}
                  </p>
                  <p className="text-xs">{employee.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {employee.equalSplitTip && (
                  <div
                    title={t("same-tip-label")}
                    className="inline-flex w-fit max-w-[170px] truncate items-center rounded-full border px-3 py-1 text-sm"
                  >
                    <span className="truncate">
                      {t("same-tip-label")}
                    </span>
                  </div>
                )}
                <div
                  className={`inline-flex w-fit whitespace-nowrap items-center rounded-full border px-3 py-1 text-sm ${
                    emtpyGroup && "border-yellow text-yellow"
                  }`}
                >
                  {!emtpyGroup
                    ? employee.userGroups
                        .filter((group) => group.id != null && group.name)
                        .map((group) => group.name)
                        .join(", ")
                    : t("branches.view.no-group")}
                </div>
                <div
                  title={employee.qrDisplayDescription ?? t("employee.qr-config.label")}
                  className="inline-flex w-fit max-w-[240px] truncate items-center rounded-full border px-3 py-1 text-sm"
                >
                  <span className="truncate">
                    {employee.qrDisplayShortName ?? t("employee.qr-config.label")}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    title={t("branches.edit-employee")}
                    onClick={() => onEditEmployee(employee)}
                    sx={{
                      margin: 0,
                      minWidth: "fit-content",
                      background: "transparent",
                      border: "1px solid #EF0BB8",
                      borderRadius: "50%",
                      color: "#EF0BB8",
                      textTransform: "none",
                      fontFamily: "Montserrat",
                      fontWeight: 300,
                      width: "2.5rem",
                      height: "2.5rem",
                      padding: "2px",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g fill="#EF0BB8">
                        <path d="M8.293 3.707 1 11v4h4l7.293-7.293-4-4ZM9.707 2.293l4 4 1.465-1.465a2.829 2.829 0 0 0-4-4L9.707 2.293Z" />
                      </g>
                    </svg>
                  </Button>
                  <ToggleEmployeeOnLeave
                    employee={employee}
                    onOnLeaveChange={onOnLeaveChange}
                  />
                  <DeleteEmployee
                    userId={employee.userId}
                    updateEmployees={onDeleteEmployee}
                    branchId={employee.branchId}
                  />
                </div>
              </div>
            </div>
          );
        })}
      
      {/* Controles de paginación */}
      {!isLoading && totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 py-4">
          <Button
            variant="outlined"
            onClick={onGoToPreviousPage}
            disabled={currentPage === 0}
            sx={{
              borderColor: "#EF0BB8",
              color: "#EF0BB8",
              fontFamily: "Montserrat",
              fontWeight: 500,
              textTransform: "none",
              paddingX: 2,
              paddingY: 1,
              borderRadius: 2,
              "&:hover": {
                borderColor: "#d009a0",
                backgroundColor: "#f8f4ff",
              },
              "&:disabled": {
                borderColor: "#ccc",
                color: "#ccc",
              },
            }}
          >
            Anterior
          </Button>
          
          <Pagination
            count={totalPages}
            page={currentPage + 1}
            onChange={(_, page) => onGoToPage(page - 1)}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                fontFamily: "Montserrat",
                fontWeight: 500,
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#EF0BB8",
                color: "white",
                "&:hover": {
                  backgroundColor: "#d009a0",
                },
              },
            }}
          />
          
          <Button
            variant="outlined"
            onClick={onGoToNextPage}
            disabled={currentPage === totalPages - 1}
            sx={{
              borderColor: "#EF0BB8",
              color: "#EF0BB8",
              fontFamily: "Montserrat",
              fontWeight: 500,
              textTransform: "none",
              paddingX: 2,
              paddingY: 1,
              borderRadius: 2,
              "&:hover": {
                borderColor: "#d009a0",
                backgroundColor: "#f8f4ff",
              },
              "&:disabled": {
                borderColor: "#ccc",
                color: "#ccc",
              },
            }}
          >
            Siguiente
          </Button>
        </div>
      )}
    </div>
  );
} 